import { useState } from 'react';
import { melbourneDate, melbourneToday, weekOf } from '../../../shared/dates';
import { SOCIAL_ASSET_CHANNELS, type SocialAssetWithProject } from '../../../shared/types';
import { addDays, BrandBadge, formatDate, formatWeekdayDate, formatWeekdayDay } from '../../components/primitives';
import { assetChannels, channelLabel, CHANNEL_LABELS, monthLabel, safeUrl, scheduleConfirmLabel, shiftMonth, toggleChannel, TYPE_LABELS, type CalendarWeek, type ScheduleDraft, type SocialActions } from '../../lib/socialCalendar';
import { FilterChip } from './chrome';

// ── Popovers ─────────────────────────────────────────────────────────────

/** Inline month picker for the schedule popover — reuses the date-picker
    day cells (.dp-*) rather than re-styling a second calendar. */
function MiniMonth({ value, onPick }: { value: string; onPick: (iso: string) => void }) {
  const [view, setView] = useState(value.slice(0, 7));
  const start = weekOf(`${view}-01`).start;
  const monthEnd = addDays(`${shiftMonth(view, 1)}-01`, -1);
  const cells: string[] = [];
  for (let iso = start; iso <= monthEnd || cells.length % 7 !== 0; iso = addDays(iso, 1)) {
    cells.push(iso);
  }
  return (
    <>
      <div className="social-pop-month">
        <button type="button" className="dp-nav" onClick={() => setView(shiftMonth(view, -1))}>
          ‹
        </button>
        <span className="f-label">Date · {monthLabel(view)}</span>
        <button type="button" className="dp-nav" onClick={() => setView(shiftMonth(view, 1))}>
          ›
        </button>
      </div>
      <div className="dp-grid">
        {['m', 't', 'w', 't', 'f', 's', 's'].map((d, i) => (
          <span className="dp-dow" key={`${d}${i}`}>
            {d}
          </span>
        ))}
        {cells.map((iso) => (
          <button
            type="button"
            key={iso}
            className={`dp-day${iso.slice(0, 7) === view ? '' : ' out'}${iso === value ? ' selected' : ''}`}
            onClick={() => onPick(iso)}
          >
            {Number(iso.slice(8, 10))}
          </button>
        ))}
      </div>
    </>
  );
}

export function SchedulePopover({
  draft,
  onChange,
  onCancel,
  onConfirm,
}: {
  draft: ScheduleDraft;
  onChange: (next: ScheduleDraft) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="social-pop" data-social-pop="schedule">
      <div className="social-pop-title">Schedule · {draft.asset.label}</div>
      <div className="f-label">Channels</div>
      <div className="social-chiprow">
        {SOCIAL_ASSET_CHANNELS.map((c) => (
          <FilterChip
            key={c}
            label={CHANNEL_LABELS[c]}
            on={draft.channels.includes(c)}
            onClick={() => onChange({ ...draft, channels: toggleChannel(draft.channels, c) })}
          />
        ))}
      </div>
      {/* Keyed on the asset so the picker re-seeds on the next row: paging
          to another month must not follow the popover to a different slot. */}
      <MiniMonth
        key={draft.asset.id}
        value={draft.date}
        onPick={(date) => onChange({ ...draft, date })}
      />
      <div className="social-pop-foot">
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn primary"
          disabled={draft.channels.length === 0}
          onClick={onConfirm}
        >
          {scheduleConfirmLabel(draft)}
        </button>
      </div>
    </div>
  );
}

export function SlotActionsPopover({
  asset,
  actions,
  onClose,
}: {
  asset: SocialAssetWithProject;
  actions: SocialActions;
  onClose: () => void;
}) {
  const context = [
    formatWeekdayDate(asset.planned_date ?? melbourneToday()),
    channelLabel(assetChannels(asset.channels)),
    asset.label,
    ...(asset.project_name !== null ? [asset.project_name] : []),
  ].join(' · ');
  const href = safeUrl(asset.url);
  // A used post is history: nothing here may put it back in the plan, or the
  // slot loses its date/channels while used_at survives. Repurpose is the
  // only route back into the calendar.
  const used = asset.status === 'used';
  return (
    <div className="social-pop" data-social-pop="slot">
      <div className="social-pop-title">{context}</div>
      <div className="social-pop-link">
        {href !== null ? (
          <a href={href} target="_blank" rel="noreferrer">
            open asset link →
          </a>
        ) : (
          <span className="dim" title={asset.url ?? undefined}>
            {asset.url ? 'Link on this asset is not a web address' : 'No link on this asset'}
          </span>
        )}
      </div>
      {used && (
        <div className="social-pop-note">
          Posted {formatDate(melbourneDate(asset.used_at ?? asset.updated_at))} · used posts stay as
          history. Repurpose makes a fresh asset from this one.
        </div>
      )}
      <div className="social-pop-foot start">
        {used ? (
          <button type="button" className="btn primary" onClick={() => actions.onRepurpose(asset)}>
            Repurpose
          </button>
        ) : (
          <>
            <button type="button" className="btn primary" onClick={() => actions.onMarkUsed(asset)}>
              Mark used today
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => actions.onStartSchedule(asset, asset.planned_date ?? undefined)}
            >
              Move date
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => actions.onStartSchedule(asset, asset.planned_date ?? undefined)}
            >
              Edit channels
            </button>
            <button type="button" className="btn" onClick={() => actions.onUnschedule(asset)}>
              Unschedule
            </button>
          </>
        )}
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

export function FillWeekPopover({
  week,
  ready,
  onPick,
  onClose,
}: {
  week: CalendarWeek;
  ready: SocialAssetWithProject[];
  onPick: (asset: SocialAssetWithProject) => void;
  onClose: () => void;
}) {
  return (
    <div className="social-pop" data-social-pop="fill">
      <div className="social-pop-title">
        Fill {formatWeekdayDay(week.start)} to {formatWeekdayDay(week.end)}
      </div>
      <div className="social-fill-items">
        {ready.map((asset) => (
          <button type="button" className="social-card" key={asset.id} onClick={() => onPick(asset)}>
            <span className="pill social-type">{TYPE_LABELS[asset.type]}</span>
            <span className="lbl">{asset.label}</span>
            <BrandBadge brand={asset.brand} />
          </button>
        ))}
      </div>
      <div className="social-pop-foot">
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
